import { useCallback, useEffect, useState } from 'react';

import http from './http';

export type ApiState<T> = {
  data: T | null;
  error: string | null;
  /** True while a request is in flight. The previous data stays visible meanwhile. */
  loading: boolean;
  /** Fetches again with the same path and params (after a change elsewhere). */
  reload: () => void;
};

/**
 * GETs `path` with `params` and re-requests when either changes. A newer request
 * cancels the older one, and the last good data is kept during a refetch so the
 * page holds its frame instead of flashing (dataviz: "refetch keeps the frame").
 * A null path skips the request.
 */
export const useApi = <T>(path: string | null, params?: Record<string, string | number | undefined>): ApiState<T> => {
  const [state, setState] = useState<Omit<ApiState<T>, 'reload'>>({ data: null, error: null, loading: path !== null });
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const key = path === null ? null : path + JSON.stringify(params ?? {});

  useEffect(() => {
    if (path === null) {
      setState({ data: null, error: null, loading: false });
      return;
    }
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    http
      .get<T>(path, { params, signal: ctrl.signal })
      .then((res) => setState({ data: res.data, error: null, loading: false }))
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        const status = (err as { response?: { status?: number } }).response?.status;
        setState((s) => ({
          data: status === 404 ? null : s.data,
          error:
            status === 404
              ? 'Not found.'
              : status === 403
                ? 'You have no access to this. Ask a Prisma admin.'
                : 'Could not reach the Prisma API. Is the backend running?',
          loading: false,
        }));
      });
    return () => ctrl.abort();
    // `key` stands for path + params: objects passed inline are new on every render.
  }, [key, version]);

  return { ...state, reload };
};

/** Returns value once it has stopped changing for `ms` (search-as-you-type). */
export const useDebounced = <T>(value: T, ms = 250): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
};
